import { useState, useEffect } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DialogModal } from "../../components/ui/dialogModal";
import { cn } from "../../utils/tailwindUtils";
import { Mail, RefreshCw, Trash2, CheckCircle, AlertCircle, WifiOff, Loader2 } from "lucide-react";
import { Checkbox } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import {
    EMAIL_ACCOUNTS_URL,
    EMAIL_GMAIL_OAUTH_URL,
    EMAIL_OUTLOOK_OAUTH_URL,
    EMAIL_PROVIDERS_URL,
    EMAIL_ACCOUNT_SYNC_URL,
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";

const PROVIDER_INFO = {
    gmail:   { label: 'Gmail',   color: 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800',   textColor: 'text-red-600 dark:text-red-400' },
    outlook: { label: 'Outlook', color: 'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800', textColor: 'text-blue-600 dark:text-blue-400' },
    imap:    { label: 'IMAP',    color: 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700',   textColor: 'text-gray-600 dark:text-gray-400' },
    cpanel:  { label: 'cPanel',  color: 'bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-800', textColor: 'text-orange-600 dark:text-orange-400' },
    godaddy: { label: 'GoDaddy', color: 'bg-teal-50 border-teal-200 dark:bg-teal-900/10 dark:border-teal-800', textColor: 'text-teal-600 dark:text-teal-400' },
};

// Password providers come from the server (email/accounts/providers/), which
// works out the IMAP/SMTP hosts for each preset. This copy is only the
// fallback if that call fails, so the dialog never opens empty.
const FALLBACK_PASSWORD_PROVIDERS = [
    { key: 'cpanel',  label: 'cPanel Email',  description: 'Email hosted on a cPanel server', fields: ['server_host'], help: '' },
    { key: 'godaddy', label: 'GoDaddy Email', description: 'GoDaddy Workspace Email or Professional Email', fields: [], help: '' },
    { key: 'imap',    label: 'IMAP / SMTP',   description: 'Manual configuration for any other provider', fields: ['imap_host', 'imap_port', 'smtp_host', 'smtp_port', 'use_ssl'], help: '' },
];

const SyncBadge = ({ status }) => {
    if (status === 'connected') return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle className="h-3 w-3" />Connected
        </span>
    );
    if (status === 'error') return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            <AlertCircle className="h-3 w-3" />Error
        </span>
    );
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
            <WifiOff className="h-3 w-3" />Disconnected
        </span>
    );
};

const IMAP_DEFAULT = {
    email_address: '', display_name: '', password: '',
    imap_host: '', imap_port: '993',
    smtp_host: '', smtp_port: '587',
    use_ssl: true,
    server_host: '',
};

export default function EmailIntegration() {
    const { fetch, create, remove } = useApiMethods();
    const [accounts, setAccounts] = useState([]);
    const [isAdminView, setIsAdminView] = useState(false);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [connectOpen, setConnectOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [provider, setProvider] = useState(null);
    const [imapForm, setImapForm] = useState(IMAP_DEFAULT);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [syncingId, setSyncingId] = useState(null);
    const [oauthLoading, setOauthLoading] = useState(false);
    const [passwordProviders, setPasswordProviders] = useState(FALLBACK_PASSWORD_PROVIDERS);


    const loadAccounts = async () => {
        setLoadingAccounts(true);
        const res = await fetch(EMAIL_ACCOUNTS_URL);
        if (res?.status === 'success') {
            setAccounts(res.data || []);
            setIsAdminView(res.is_admin_view || false);
        }
        setLoadingAccounts(false);
    };

    useEffect(() => {
        loadAccounts();
        fetch(EMAIL_PROVIDERS_URL).then(res => {
            if (res?.status === 'success' && res.data?.length) setPasswordProviders(res.data);
        });
    }, []);

    const passwordProvider = passwordProviders.find(p => p.key === provider);
    const hasField = (f) => passwordProvider?.fields?.includes(f);

    const openConnect = () => {
        setStep(1);
        setProvider(null);
        setImapForm(IMAP_DEFAULT);
        setConnectOpen(true);
    };

    const handleOAuth = async (oauthUrl) => {
        setOauthLoading(true);
        try {
            const res = await fetch(oauthUrl);
            if (res?.status === 'success' && res.auth_url) {
                window.open(res.auth_url, '_blank');
                setConnectOpen(false);
                ToastMsg('info', 'Complete authorization in the new tab, then refresh this page.');
            } else {
                ToastMsg('error', res?.message || 'Failed to get authorization URL');
            }
        } catch (e) {
            ToastMsg('error', 'OAuth request failed');
        }
        setOauthLoading(false);
    };

    const handleImapConnect = async () => {
        if (!imapForm.email_address || !imapForm.password) {
            ToastMsg('error', 'Email address and password are required');
            return;
        }
        if (hasField('imap_host') && !imapForm.imap_host) {
            ToastMsg('error', 'IMAP host is required');
            return;
        }
        setSaving(true);
        const res = await create(EMAIL_ACCOUNTS_URL, { ...imapForm, provider });
        setSaving(false);
        if (res?.status === 'success') {
            const d = res.data || {};
            ToastMsg('success', `${passwordProvider?.label || 'Mailbox'} ${d.reconnected ? 'reconnected' : 'connected'}`);
            // Receiving works at this point; sending is checked separately and
            // a failure there keeps the mailbox out of the campaign pool.
            if (d.smtp_verified === false) {
                ToastMsg('warning', `Receiving works, but sending could not be verified: ${d.smtp_error || 'unknown error'}`);
            }
            setConnectOpen(false);
            loadAccounts();
        } else {
            ToastMsg('error', res?.message || 'Connection failed');
        }
    };

    // Manual sync: reads new Inbox + Sent mail and keeps what involves a lead.
    // Large mailboxes come back with `more`, and pressing again continues.
    const handleSync = async (acc) => {
        setSyncingId(acc.id);
        const res = await create(EMAIL_ACCOUNT_SYNC_URL(acc.id), {});
        setSyncingId(null);
        if (res?.status === 'success') {
            const d = res.data || {};
            ToastMsg(d.stored ? 'success' : 'info',
                `${d.stored || 0} lead email${d.stored === 1 ? '' : 's'} synced from ${d.scanned || 0} checked`
                + (d.more ? ' — more waiting, press Sync now again' : ''));
        } else {
            ToastMsg('error', res?.message || 'Sync failed');
        }
        loadAccounts();
    };

    const canSync = (acc) => !['gmail', 'outlook'].includes(acc.provider);

    const SyncNowButton = ({ acc }) => canSync(acc) && (
        <button
            type="button"
            onClick={() => handleSync(acc)}
            disabled={syncingId === acc.id}
            className="flex items-center gap-1 text-[11px] text-primary hover:opacity-80 font-medium disabled:opacity-60"
        >
            <RefreshCw className={cn("h-3 w-3", syncingId === acc.id && "animate-spin")} />
            {syncingId === acc.id ? 'Syncing…' : 'Sync now'}
        </button>
    );

    const handleDisconnect = async (id) => {
        setDeletingId(id);
        const res = await remove(`${EMAIL_ACCOUNTS_URL}?id=${id}`);
        setDeletingId(null);
        if (res?.status === 'success') {
            ToastMsg('success', 'Account disconnected');
            loadAccounts();
        } else {
            ToastMsg('error', res?.message || 'Failed to disconnect');
        }
    };

    const imapChange = (field, value) => setImapForm(prev => ({ ...prev, [field]: value }));

    return (
        <div className="p-6 space-y-6">
            <ContainerCard>
                <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Email Integration</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            {isAdminView
                                ? 'All connected email accounts across users'
                                : 'Connect your mailbox to send tracked emails from Gro Wing CRM'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={loadAccounts}
                            disabled={loadingAccounts}
                            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors"
                            title="Refresh"
                        >
                            <RefreshCw className={cn("h-4 w-4", loadingAccounts && "animate-spin")} />
                        </button>
                        {/* Always available. This was hidden behind !isAdminView,
                            and the backend sets is_admin_view for any admin or
                            super_admin — so the one person responsible for
                            configuring mailboxes, including the shared ones the
                            campaign sending pool draws from, was the only one
                            who could not add one. Admins still get the
                            all-users table below; they can now connect too. */}
                        <Button onClick={openConnect} className="flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4" /> Connect Account
                        </Button>
                    </div>
                </div>

                {loadingAccounts ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                ) : accounts.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                            <Mail className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                        </div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No accounts connected</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            Connect Gmail, Outlook, cPanel, GoDaddy or any IMAP mailbox to start sending tracked emails
                        </p>
                        <Button onClick={openConnect} className="mt-4 inline-flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4" /> Connect Account
                        </Button>
                    </div>
                ) : isAdminView ? (
                    /* Admin table view */
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-gray-700">
                                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Provider</th>
                                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email Account</th>
                                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Synced</th>
                                    <th className="py-2.5 px-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {accounts.map(acc => {
                                    const info = PROVIDER_INFO[acc.provider] || PROVIDER_INFO.imap;
                                    return (
                                        <tr key={acc.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                            <td className="py-3 px-3">
                                                <p className="font-medium text-gray-800 dark:text-gray-100">{acc.user_name || '—'}</p>
                                                {acc.user_email && (
                                                    <p className="text-xs text-gray-400 dark:text-gray-500">{acc.user_email}</p>
                                                )}
                                            </td>
                                            <td className="py-3 px-3">
                                                <span className={cn("text-xs font-bold uppercase tracking-wider", info.textColor)}>
                                                    {acc.provider_label || info.label}
                                                </span>
                                            </td>
                                            <td className="py-3 px-3">
                                                <p className="font-medium text-gray-800 dark:text-gray-100">{acc.email_address}</p>
                                                {acc.display_name && (
                                                    <p className="text-xs text-gray-400 dark:text-gray-500">{acc.display_name}</p>
                                                )}
                                            </td>
                                            <td className="py-3 px-3">
                                                <SyncBadge status={acc.sync_status} />
                                            </td>
                                            <td className="py-3 px-3 text-xs text-gray-400 dark:text-gray-500">
                                                {acc.last_synced_at ? new Date(acc.last_synced_at).toLocaleString() : '—'}
                                                {acc.sync_error && (
                                                    <p className="text-[10px] text-red-500 mt-0.5 max-w-xs truncate" title={acc.sync_error}>{acc.sync_error}</p>
                                                )}
                                            </td>
                                            <td className="py-3 px-3 text-right">
                                                <div className="flex items-center justify-end gap-3">
                                                <SyncNowButton acc={acc} />
                                                <button
                                                    onClick={() => handleDisconnect(acc.id)}
                                                    disabled={deletingId === acc.id}
                                                    className="flex items-center gap-1 text-[11px] text-red-500 hover:text-red-700 dark:hover:text-red-400 font-medium ml-auto"
                                                >
                                                    {deletingId === acc.id
                                                        ? <Loader2 className="h-3 w-3 animate-spin" />
                                                        : <Trash2 className="h-3 w-3" />}
                                                    Disconnect
                                                </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    /* Regular user card view */
                    <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
                        {accounts.map(acc => {
                            const info = PROVIDER_INFO[acc.provider] || PROVIDER_INFO.imap;
                            return (
                                <div key={acc.id} className={cn("p-4 rounded-xl border-2", info.color)}>
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <span className={cn("text-xs font-bold uppercase tracking-wider", info.textColor)}>
                                                {acc.provider_label || info.label}
                                            </span>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mt-0.5">
                                                {acc.email_address}
                                            </p>
                                            {acc.display_name && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400">{acc.display_name}</p>
                                            )}
                                        </div>
                                        <SyncBadge status={acc.sync_status} />
                                    </div>
                                    {canSync(acc) && (
                                        <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-1">
                                            {acc.last_synced_at
                                                ? `Last synced: ${new Date(acc.last_synced_at).toLocaleString()}`
                                                : 'Not synced yet — press Sync now'}
                                        </p>
                                    )}
                                    {acc.sync_error && (
                                        <p className="text-[10px] text-red-500 dark:text-red-400 mb-1" title={acc.sync_error}>
                                            Sync failed: {acc.sync_error}
                                        </p>
                                    )}
                                    {acc.provider !== 'gmail' && acc.provider !== 'outlook' && (
                                        <p className={cn("text-[10px] mb-3", acc.smtp_verified_at ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400")}
                                           title={acc.smtp_last_error || ''}>
                                            {acc.smtp_verified_at ? `Sending verified · ${acc.smtp_host}` : 'Sending not verified'}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                                        <SyncNowButton acc={acc} />
                                        <button
                                            onClick={() => handleDisconnect(acc.id)}
                                            disabled={deletingId === acc.id}
                                            className="flex items-center gap-1 text-[11px] text-red-500 hover:text-red-700 dark:hover:text-red-400 font-medium"
                                        >
                                            {deletingId === acc.id
                                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                                : <Trash2 className="h-3 w-3" />}
                                            Disconnect
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </ContainerCard>

            {/* Connect Dialog */}
            <DialogModal
                open={connectOpen}
                onClose={() => setConnectOpen(false)}
                title="Connect Email Account"
                width="520px"
                showActions={false}
            >
                {step === 1 && (
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">Choose your email provider:</p>
                        <div className="grid grid-cols-1 gap-3">
                            {[
                                { key: 'gmail',   label: 'Gmail',   desc: 'Connect via Google OAuth' },
                                { key: 'outlook', label: 'Outlook', desc: 'Microsoft 365 / Outlook, including Microsoft 365 from GoDaddy' },
                                ...passwordProviders.map(p => ({ key: p.key, label: p.label, desc: p.description })),
                            ].map(p => (
                                <button
                                    key={p.key}
                                    onClick={() => { setProvider(p.key); setStep(2); }}
                                    className="flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 text-left transition-all"
                                >
                                    <Mail className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{p.label}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{p.desc}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {step === 2 && (provider === 'gmail' || provider === 'outlook') && (
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            You'll be redirected to{' '}
                            <strong>{provider === 'gmail' ? 'Google' : 'Microsoft'}</strong> to authorize access.
                        </p>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setStep(1)} className="flex-1 text-sm">
                                Back
                            </Button>
                            <Button
                                onClick={() => handleOAuth(provider === 'gmail' ? EMAIL_GMAIL_OAUTH_URL : EMAIL_OUTLOOK_OAUTH_URL)}
                                disabled={oauthLoading}
                                className="flex-1 text-sm flex items-center gap-2 justify-center"
                            >
                                {oauthLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                                Authorize with {provider === 'gmail' ? 'Google' : 'Microsoft'}
                            </Button>
                        </div>
                    </div>
                )}

                {step === 2 && passwordProvider && (
                    <div className="space-y-3">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{passwordProvider.label}</p>
                        {passwordProvider.help && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5">{passwordProvider.help}</p>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Email Address *</label>
                                <Input value={imapForm.email_address} onChange={e => imapChange('email_address', e.target.value)} placeholder="you@example.com" />
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Display Name</label>
                                <Input value={imapForm.display_name} onChange={e => imapChange('display_name', e.target.value)} placeholder="My Work Email" />
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Password *</label>
                                <Input type="password" value={imapForm.password} onChange={e => imapChange('password', e.target.value)} placeholder="••••••••" />
                            </div>
                            {hasField('server_host') && (
                                <div className="col-span-2">
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Server Hostname (optional)</label>
                                    <Input value={imapForm.server_host} onChange={e => imapChange('server_host', e.target.value)} placeholder="e.g. server123.yourhost.com — blank uses mail.yourdomain" />
                                </div>
                            )}
                            {hasField('imap_host') && (<>
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">IMAP Host *</label>
                                <Input value={imapForm.imap_host} onChange={e => imapChange('imap_host', e.target.value)} placeholder="imap.example.com" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">IMAP Port</label>
                                <Input type="number" value={imapForm.imap_port} onChange={e => imapChange('imap_port', e.target.value)} placeholder="993" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">SMTP Host</label>
                                <Input value={imapForm.smtp_host} onChange={e => imapChange('smtp_host', e.target.value)} placeholder="smtp.example.com" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">SMTP Port</label>
                                <Input type="number" value={imapForm.smtp_port} onChange={e => imapChange('smtp_port', e.target.value)} placeholder="587" />
                            </div>
                            <div className="col-span-2">
                                <Checkbox checked={imapForm.use_ssl} onChange={e => imapChange('use_ssl', e.target.checked)}>
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Use SSL</span>
                                </Checkbox>
                            </div>
                            </>)}
                        </div>
                        {!hasField('imap_host') && (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                Server settings are detected automatically. Connecting can take up to a minute while servers are tried.
                            </p>
                        )}
                        <div className="flex gap-2 pt-2">
                            <Button variant="outline" onClick={() => setStep(1)} className="flex-1 text-sm">Back</Button>
                            <Button onClick={handleImapConnect} disabled={saving} className="flex-1 text-sm flex items-center gap-2 justify-center">
                                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                                {saving ? 'Testing connection…' : 'Connect'}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogModal>
        </div>
    );
}
